// Sentry init must run before anything else so the error boundary
// catches even module-init failures. Audit HIGH 1.2 / 3.1.
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: import.meta.env['VITE_SENTRY_DSN'],
  environment: import.meta.env['VITE_SENTRY_ENVIRONMENT']
    ?? (import.meta.env.DEV ? 'development' : 'production'),
  release: import.meta.env['VITE_SENTRY_RELEASE'],
  tracesSampleRate: 0,
  beforeSend(event) {
    // Same scrub the api's beforeSend applies (EVII retention audit):
    // if any integration ever attaches a request body, credential and
    // conversation-bearing fields must not ride to Sentry. The browser
    // SDK rarely populates request.data, so this is a cheap guarantee.
    if (event.request?.data && typeof event.request.data === 'object') {
      const data = event.request.data as Record<string, unknown>;
      delete data['password'];
      delete data['message'];
      delete data['text'];
      delete data['transcript'];
    }
    return event;
  },
});

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@evi/skins/styles.css';
import { ThemeProvider } from '@evi/skins';
import '@evi/ui/theme.css';
import './lib/theme.css';
import './lib/mobile.css';
import { App } from './App';
import { HostCampaignPage } from './pages/host-campaign/HostCampaignPage';
import { canonicalHostProgramUrl, HOST_PROGRAM_PATH } from '@evi/shared';
import { LiveUpdatesProvider } from './components/LiveUpdatesProvider';
import { initInstallPrompt } from '@/lib/pwaInstall';

// Defensive cleanup: the PWA's auth key (`evi-web-auth`) has no
// business existing on the admin's origin localStorage. The Vite
// configs are now hard-pinned to distinct ports (admin :5174,
// PWA :5173) via strictPort, so cross-app localStorage sharing
// should be impossible going forward — but this removeItem call
// cleans up any orphan key from before that pin landed, on every
// boot. removeItem is a no-op when the key is absent.
try {
  localStorage.removeItem('evi-web-auth');
  // `evi-theme` was the persist key for the legacy useThemeStore
  // Zustand (deleted during the dashboard redesign). Theme state
  // now lives in @evi/skins' ThemeProvider, which uses its own
  // storage. Remove the orphan key so it doesn't accumulate.
  localStorage.removeItem('evi-theme');
  // `evi.admin.dashboard.layout.v1` and `.v2` were retired by
  // subsequent schema bumps (v1 -> v2 fixed the mobile chart_revenue
  // ordering; v2 -> v3 reordered the KPI 2x2 to Revenue+Energy on
  // top, Chargers+Active Sessions on the bottom). Reap the orphans
  // so they don't sit forever in localStorage.
  localStorage.removeItem('evi.admin.dashboard.layout.v1');
  localStorage.removeItem('evi.admin.dashboard.layout.v2');
} catch {
  // SSR / private-mode / quota-exceeded — none of which apply on
  // admin in practice, but the swallow is defensive against
  // localStorage throwing during boot. The cleanup is best-effort.
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── PWA install + service worker ──────────────────────────────────────────
// Capture the (Android/desktop) install prompt at boot — it can fire before
// React mounts; the Settings "Install EVI Admin" button reads it. Mirrors the
// driver app's main.tsx. See lib/pwaInstall.
initInstallPrompt();

// Register the minimal passthrough service worker (public/sw.js): no caching,
// it exists only to satisfy Chrome's install criteria. PROD only so the Vite
// dev server isn't shadowed by a worker; deferred to load so it never competes
// with first paint; guarded for browsers without SW support.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Non-fatal — install is a progressive enhancement, not required to run.
    });
  });
}

// Cloudflare Web Analytics — production builds only. Mirror of the
// driver app's beacon block in apps/web/src/main.tsx: runtime
// injection (Vite's index.html interpolation can't gate on
// import.meta.env.PROD), token hardcoded because Cloudflare's beacon
// design embeds it publicly in every instrumented page — safe to
// commit. Same token as the driver app; the Cloudflare Web Analytics
// site must list the admin hostname (admin.stg today, the prod admin
// host at cutover) for reports to attribute correctly.
if (import.meta.env.PROD) {
  const cfBeacon = document.createElement('script');
  cfBeacon.defer = true;
  cfBeacon.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  cfBeacon.setAttribute(
    'data-cf-beacon',
    '{"token": "435aeae3dad14e859a08db5346fd9036"}',
  );
  document.head.appendChild(cfBeacon);
}

// Microsoft Clarity — session analytics, env-driven. Mirror of the
// driver app's block in apps/web/src/main.tsx, keyed on
// VITE_CLARITY_ID. Unset for the admin today (no admin Clarity
// project yet), so this loads nothing until an admin project id is set
// in the Admin StaticSite environment (infra/sst.config.ts).
const clarityId = import.meta.env['VITE_CLARITY_ID'] as string | undefined;
if (clarityId) {
  const w = window as unknown as Record<string, unknown>;
  if (!w['clarity']) {
    const fn = (...args: unknown[]): void => {
      const q = ((fn as { q?: unknown[] }).q ??= []);
      q.push(args);
    };
    w['clarity'] = fn;
  }
  const clarityScript = document.createElement('script');
  clarityScript.async = true;
  clarityScript.src = `https://www.clarity.ms/tag/${clarityId}`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(clarityScript, firstScript);
}

const hostAlias = canonicalHostProgramUrl(window.location);
if (hostAlias) window.history.replaceState(window.history.state, '', hostAlias);

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 24, fontFamily: 'system-ui', textAlign: 'center' }}>
          <h1 style={{ fontSize: 18, marginBottom: 8 }}>Something went wrong.</h1>
          <p style={{ fontSize: 14, color: '#888', marginBottom: 16 }}>
            Reload to try again. The error was reported to monitoring.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              fontSize: 14,
              border: '1px solid #888',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      }
    >
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {/* Live cards: one /admin/live stream invalidates the queries on screen. */}
          {window.location.pathname === HOST_PROGRAM_PATH ? <HostCampaignPage /> : <LiveUpdatesProvider>
            <App />
          </LiveUpdatesProvider>}
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
